<template>
  <v-dialog v-model="dialog" width="500">
    <v-card class="planner-entry-form" rounded="lg">
      <!-- Header with close button -->
      <div class="d-flex justify-space-between align-center pa-4">
        <div class="d-flex align-center">
          <v-icon :icon="getPlannerTypeIcon(plannerEntry?.type)" size="24" :color="settingsStore.settings.buttonColor" class="me-4"></v-icon>
          <span class="text-h6" :style="{ color: settingsStore.settings.buttonColor }">{{ plannerEntry?.name || '' }}</span>
        </div>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon icon="fa fa-times"></v-icon>
        </v-btn>
      </div>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <v-alert
          v-if="formError"
          type="error"
          class="mb-4"
        >
          {{ formError }}
        </v-alert>

        <!-- Date and Time Display (Read-only) -->
        <div class="mb-4">
          <div class="text-caption text-grey-darken-1">
            {{ formattedDateTime }}
          </div>
        </div>

        <!-- Description -->
        <div v-if="plannerEntry?.description" class="mb-4">
          <div class="text-subtitle-2 mb-2" :style="{ color: settingsStore.settings.primaryColor }">
            Description
          </div>
          <div class="description-text pa-3" :style="{ 
            backgroundColor: hexToRgba(settingsStore.settings.primaryColor, 0.05),
            border: `1px solid ${hexToRgba(settingsStore.settings.primaryColor, 0.2)}`,
            borderRadius: '8px'
          }">
            {{ plannerEntry.description }}
          </div>
        </div>

        <!-- Status Actions -->
        <div class="mb-4">
          <div class="text-subtitle-2 mb-3" :style="{ color: settingsStore.settings.primaryColor }">
            Update Status
          </div>
          <div class="d-flex gap-3">
            <v-btn
              :variant="currentStatus === 'completed' ? 'flat' : 'outlined'"
              :color="currentStatus === 'completed' ? 'green' : 'grey'"
              rounded="lg"
              @click="updateStatus('completed')"
              :disabled="isSubmitting"
              class="flex-1"
            >
              <v-icon start size="small">fa fa-check</v-icon>
              {{ currentStatus === 'completed' ? 'Unmark Completed' : 'Mark Completed' }}
            </v-btn>
            <v-btn
              :variant="currentStatus === 'ignored' ? 'flat' : 'outlined'"
              :color="currentStatus === 'ignored' ? 'grey-darken-2' : 'grey'"
              rounded="lg"
              @click="updateStatus('ignored')"
              :disabled="isSubmitting"
              class="flex-1"
            >
              <v-icon start size="small">fa fa-times</v-icon>
              {{ currentStatus === 'ignored' ? 'Unmark Ignored' : 'Mark Ignored' }}
            </v-btn>
          </div>
        </div>

        <!-- Comment Section -->
        <div class="mb-4">
          <div class="text-subtitle-2 mb-2" :style="{ color: settingsStore.settings.primaryColor }">
            Client Comment
          </div>
          <v-textarea
            v-model="comment"
            label="Share your thoughts about this planner entry..."
            :style="`label-color: ${settingsStore.settings.primaryColor}`"
            variant="outlined"
            rows="4"
            :maxlength="250"
            counter="250"
            placeholder="Add your comment here..."
            class="mb-2"
          ></v-textarea>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <div class="d-flex gap-3 w-100">
          <v-btn
            v-if="comment.trim()"
            :style="{
              backgroundColor: settingsStore.settings.buttonColor,
              color: 'white'
            }"
            class="text-white primary-button flex-1"
            rounded="lg"
            @click="saveComment"
            :disabled="isSubmitting"
          >
            <v-icon start size="small">fa fa-save</v-icon>
            Save Comment
          </v-btn>
          <v-btn
            variant="outlined"
            :color="settingsStore.settings.buttonColor"
            class="flex-1"
            rounded="lg"
            @click="closeDialog"
          >
            Close
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useSettingsStore } from "@/api/store/settings";
import { healthPlannerService, type PlannerEntry } from "@/api/services/healthPlanner";
import { hexToRgba } from "@/tools/design";

interface Props {
  modelValue: boolean;
  plannerEntry?: PlannerEntry | null;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'status-updated'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const settingsStore = useSettingsStore();

// Form state
const dialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
});

const comment = ref('');
const isSubmitting = ref(false);
const formError = ref<string | null>(null);
const currentStatus = ref<string | null>(null);

// Helper function to get planner type icon
const getPlannerTypeIcon = (plannerType?: string) => {
  switch (plannerType) {
    case 'Exercise':
      return 'fa fa-star';
    case 'Food':
      return 'fa fa-cutlery';
    case 'Drink':
      return 'fa fa-glass';
    case 'Lifestyle':
      return 'fa fa-heart';
    default:
      return 'fa fa-calendar';
  }
};

// Format the current date and time
const formattedDateTime = computed(() => {
  if (!props.plannerEntry?.scheduled_datetime) return '';
  const date = new Date(props.plannerEntry.scheduled_datetime);
  return date.toLocaleString('default', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
});

const updateStatus = async (status: 'completed' | 'ignored') => {
  if (!props.plannerEntry?.id) return;

  isSubmitting.value = true;
  formError.value = null;

  try {
    // Toggle behavior: if clicking the same status, set it back to pending
    const newStatus = currentStatus.value === status ? 'pending' : status;
    
    await healthPlannerService.updateStatus(
      props.plannerEntry.id, 
      newStatus, 
      props.plannerEntry.scheduled_datetime
    );
    currentStatus.value = newStatus; // Update local status immediately
    emit('status-updated');
    // Don't close dialog - let user add comments or manually close
  } catch (err: any) {
    console.error('Error updating planner entry status:', err);
    formError.value = err.response?.data?.message || 'Failed to update status. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const saveComment = async () => {
  if (!props.plannerEntry?.id || !comment.value.trim()) return;

  isSubmitting.value = true;
  formError.value = null;

  try {
    await healthPlannerService.saveComment(props.plannerEntry.id, comment.value.trim());
    comment.value = ''; // Clear comment after saving
    emit('status-updated'); // Refresh the entry data
  } catch (err: any) {
    console.error('Error saving comment:', err);
    formError.value = err.response?.data?.message || 'Failed to save comment. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const closeDialog = () => {
  dialog.value = false;
  formError.value = null;
  // Don't clear comment.value - it will be reset when plannerEntry changes
};

// Watch for planner entry changes to load existing comment and status
watch(() => props.plannerEntry, (newEntry) => {
  comment.value = newEntry?.practitioner_comment || '';
  currentStatus.value = newEntry?.status || null;
  formError.value = null;
}, { immediate: true });
</script>

<style scoped>
.planner-entry-form {
  border-radius: 16px;
}

.practitioner-comment {
  border-radius: 8px;
}

.description-text {
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.87);
}

:deep(.v-field) {
  border-radius: 12px !important;
}

:deep(.v-textarea .v-field__input) {
  min-height: 120px !important;
  padding-top: 12px;
}

:deep(.v-label) {
  font-size: 14px;
  font-weight: 500;
}

:deep(.v-btn) {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
}

.flex-1 {
  flex: 1;
}
</style> 